import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const EVENT_NAME = "srcCopyOverridesChange";
const TABLE = "site_copy_overrides";

type Overrides = Record<string, string>;

// Module-level cache shared by all useCopyOverride hooks.
const cache: Overrides = {};
let hydrated = false;
let hydrating: Promise<void> | null = null;

function notify() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
}

async function hydrate(): Promise<void> {
  if (hydrated) return;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select("key, value");
      if (!error && Array.isArray(data)) {
        for (const row of data as { key: string; value: string }[]) {
          cache[row.key] = row.value;
        }
      }
    } catch {
      /* ignore — fall back to defaults */
    } finally {
      hydrated = true;
      notify();
    }
  })();
  return hydrating;
}

function subscribeRealtime() {
  if (typeof window === "undefined") return;
  try {
    supabase
      .channel("site_copy_overrides_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: TABLE },
        (payload: { eventType: string; new: { key?: string; value?: string } | null; old: { key?: string } | null }) => {
          if (payload.eventType === "DELETE") {
            const k = payload.old?.key;
            if (k) delete cache[k];
          } else {
            const row = payload.new;
            if (row?.key && typeof row.value === "string") {
              cache[row.key] = row.value;
            }
          }
          notify();
        },
      )
      .subscribe();
  } catch {
    /* ignore */
  }
}

// Kick off hydration + realtime once on first import in the browser.
if (typeof window !== "undefined") {
  hydrate();
  subscribeRealtime();
}

export function getOverride(key: string): string | null {
  return Object.prototype.hasOwnProperty.call(cache, key) ? cache[key] : null;
}

export async function setOverride(key: string, value: string): Promise<void> {
  // Optimistic local update so admin sees change immediately.
  const previous = cache[key];
  cache[key] = value;
  notify();
  try {
    const { error } = await supabase
      .from(TABLE)
      .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
    if (error) throw error;
  } catch (err) {
    // Revert on failure (likely non-admin / RLS denial).
    if (previous === undefined) {
      delete cache[key];
    } else {
      cache[key] = previous;
    }
    notify();
    console.error("setOverride failed:", err);
  }
}

export async function clearOverride(key: string): Promise<void> {
  const previous = cache[key];
  delete cache[key];
  notify();
  try {
    const { error } = await supabase.from(TABLE).delete().eq("key", key);
    if (error) throw error;
  } catch (err) {
    if (previous !== undefined) cache[key] = previous;
    notify();
    console.error("clearOverride failed:", err);
  }
}

export function getAllOverrides(): Overrides {
  return { ...cache };
}

export function useCopyOverride(key: string, fallback: string): string {
  const [value, setValue] = useState<string>(() => {
    const v = getOverride(key);
    return v ?? fallback;
  });

  useEffect(() => {
    const sync = () => {
      const v = getOverride(key);
      setValue(v ?? fallback);
    };
    // Ensure hydration is in-flight and sync once it lands.
    if (!hydrated) {
      hydrate().then(sync);
    } else {
      sync();
    }
    window.addEventListener(EVENT_NAME, sync);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
    };
  }, [key, fallback]);

  return value;
}

export const COPY_DEFAULTS = {
  "banner.desktop":
    "🎃 As Low as $79/Night Safari Cabins this Halloween! Prices so low it's scary! - LEARN MORE 👻",
  "banner.mobile":
    "🎃 As Low as $79/Night Safari Cabins this Halloween! Prices so low it's scary! - LEARN MORE 👻",
  "banner.href": "/blog/halloween-safari-cabins",
  "popup.headline": "Spending the day at the creek?",
  "popup.price": "Cabana Rentals Only $149 (Includes Entry for 8)",
  "popup.subline": "+ Kayak + Paddleboard from $20",
  "popup.fineprint": "",
  "popup.cta": "Reserve a Cabana",
  "popup.accommodations": "cabana",
  "popup.href": "/book-now",
  "popup.image": "",
  "popup.images": "",
  "popup.cards": "",
  "banner.bg": "",
  "banner.fg": "",
  "popup.accent": "",
  "popup.buttonFg": "",
  "banner.enabled": "true",
  "banner.startAt": "",
  "banner.endAt": "",
  "popup.enabled": "true",
  "popup.startAt": "",
  "popup.endAt": "",
} as const;

/** Reads `?promo_preview=banner|popup` from the current URL. */
export function getPromoPreview(): "banner" | "popup" | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("promo_preview");
    return v === "banner" || v === "popup" ? v : null;
  } catch {
    return null;
  }
}

/** Sanitize a remotely-supplied CSS color: empty/too long -> undefined. */
export function sanitizeColor(value: string): string | undefined {
  const v = (value || "").trim();
  if (!v || v.length > 40) return undefined;
  return v;
}


/** Parse an ISO datetime override; unparseable/empty -> null. */
function parseWindow(value: string | null): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

/** Pure check: is the banner/popup currently active per enabled + schedule? */
export function checkPromoActive(prefix: "banner" | "popup"): boolean {
  if (getPromoPreview() === prefix) return true;
  if (getOverride(`${prefix}.enabled`) === "false") return false;
  const now = Date.now();
  const start = parseWindow(getOverride(`${prefix}.startAt`));
  if (start !== null && start > now) return false;
  const end = parseWindow(getOverride(`${prefix}.endAt`));
  if (end !== null && end < now) return false;
  return true;
}

/** Reactive version: re-evaluates on override changes and every 60s. */
export function isPromoActive(prefix: "banner" | "popup"): boolean {
  const [active, setActive] = useState<boolean>(() => checkPromoActive(prefix));

  useEffect(() => {
    const sync = () => setActive(checkPromoActive(prefix));
    if (!hydrated) {
      hydrate().then(sync);
    } else {
      sync();
    }
    window.addEventListener(EVENT_NAME, sync);
    const interval = setInterval(sync, 60_000);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      clearInterval(interval);
    };
  }, [prefix]);

  return active;
}

export type CopyKey = keyof typeof COPY_DEFAULTS;

/** Force a re-render of all useCopyOverride hooks. */
export function notifyOverrideChange() {
  notify();
}

export function useAllOverrides(): [Overrides, () => void] {
  const [all, setAll] = useState<Overrides>(() => ({ ...cache }));
  const refresh = useCallback(() => setAll({ ...cache }), []);
  useEffect(() => {
    const sync = () => setAll({ ...cache });
    sync();
    window.addEventListener(EVENT_NAME, sync);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
    };
  }, []);
  return [all, refresh];
}
